// AddUnitPageNew.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import UIPageTitle from '@/components/shared/UIPageTitle';
import UIButton from '@/components/shared/UIButton';
import UIIconButton from '@/components/shared/UIIconButton';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import PropertyOverview from '@/components/dashboard/Property/PropertyOverview';
import AddUnitFormSection from '@/components/dashboard/AddUnit/AddUnitFormSection';
import { ListOfUnits } from '@/components/dashboard/AddUnit/ListOfUnits';
import CreateLeaseInviteModal from '@/components/dashboard/AddUnit/CreateLeaseInviteModal';
import useProperty from '@/hooks/useProperty';
import { useCollapsibleSections } from '@/hooks/useCollapsibleSections';
import { profileSelector } from '@/store/profile/profileSlices';
import { type IProperty } from '@/types';
import './AddUnitPage.scss';

const baseClass = 'AddUnitPage';

// Компонент хедера сторінки
interface AddUnitPageHeaderProps {
  property?: IProperty | null;
  propertyId?: string | number;
  loading?: boolean;
  className?: string;
  onBackClick?: () => void;
  onPropertyInfoClick?: () => void;
}

const AddUnitPageHeader: React.FC<AddUnitPageHeaderProps> = ({
                                                               property,
                                                               propertyId,
                                                               loading = false,
                                                               className = '',
                                                               onBackClick,
                                                               onPropertyInfoClick,
                                                             }) => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(`/dashboard/properties/${propertyId}`);
    }
  };

  const handlePropertyInfoClick = () => {
    if (onPropertyInfoClick) {
      onPropertyInfoClick();
    } else {
      navigate(`/dashboard/properties/${propertyId}`);
    }
  };

  // Заголовок з property name
  const titleText = property?.name
    ? `Property: ${property.name}`
    : 'Add Unit to Property';

  // Кнопки дій
  const renderActions = () => {
    return (
      <div className={`${baseClass}__header-actions`}>
        <UIButton
          variant="primary"
          outline
          size="medium"
          onClick={handleBackClick}
          disabled={loading}
        >
          Back
        </UIButton>

        <UIIconButton
          variant="info"
          size="medium"
          iconType="info"
          text="Property information"
          onClick={handlePropertyInfoClick}
          disabled={loading}
        />
      </div>
    );
  };

  return (
    <div className={`${baseClass}__header ${className}`.trim()}>
      <UIPageTitle
        title={titleText}
        action={renderActions()}
        showDivider={true}
        size="medium"
        variant="default"
        className={`${baseClass}__header-title`}
      />
    </div>
  );
};

// Основна сторінка
const AddUnitPage = () => {
  const navigate = useNavigate();
  const profile = useSelector(profileSelector);
  const { propertyId, property, isLoading, isFetching } = useProperty();

  // Стан для тригеру рефреша списку юнітів
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Стан для модалю додавання тенанта
  const [addTenantModal, setAddTenantModal] = useState<{
    isOpen: boolean;
    unitId: number | null;
    unitNumber: string;
  }>({
    isOpen: false,
    unitId: null,
    unitNumber: ''
  });

  // Керування станами згорнутості секцій
  const { getSectionState, toggleSection } = useCollapsibleSections(
    'add-unit-page',
    {
      overview: false,
      addUnit: false,
    },
  );

  // Обробники дій з юнітами
  const handleUnitAction = (unitId: string, action: string, unitNumber?: string) => {
    console.log('Unit action:', action, 'for unit:', unitId);

    switch (action) {
      case 'add-tenant':
        const numericUnitId = parseInt(unitId, 10);
        setAddTenantModal({
          isOpen: true,
          unitId: numericUnitId,
          unitNumber: unitNumber || unitId // Використовуємо реальний номер
        });
        break;
      case 'tenant-view':
        // Перехід до профілю тенанта
        console.log('Viewing tenant for unit:', unitId);
        break;
      case 'tenant-edit':
        // Редагування тенанта
        console.log('Editing tenant for unit:', unitId);
        break;
      case 'info':
        // Перегляд інформації про юніт
        console.log('Viewing unit info:', unitId);
        // Можна відкрити детальну інформацію про юніт
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleAddUnit = () => {
    console.log('Add new unit button clicked');
    // Можна розгорнути секцію додавання юнітів
    if (getSectionState('addUnit')) {
      toggleSection('addUnit');
    }
  };

  // Обробник успішного додавання юнітів
  const handleUnitsAdded = (addedUnits: any[]) => {
    console.log('Units added successfully:', addedUnits);

    // Тригеруємо рефреш списку юнітів через зміну key
    setRefreshTrigger(prev => prev + 1);
  };

  // Обробник успішного додавання тенанта
  const handleTenantAdded = () => {
    console.log('Tenant added successfully');

    // Тригеруємо рефреш списку юнітів
    setRefreshTrigger(prev => prev + 1);
  };

  // Обробник закриття модалю
  const handleCloseAddTenantModal = () => {
    setAddTenantModal({
      isOpen: false,
      unitId: null,
      unitNumber: ''
    });
  };

  // Обробник очищення форми
  const handleFormClear = () => {
    console.log('Form cleared');
  };

  if (isFetching || isLoading) {
    return (
      <Card className={baseClass}>
        <h4>Loading...</h4>
      </Card>
    );
  }

  if (!propertyId || (!(isLoading || isFetching) && !property)) {
    return (
      <Card className={baseClass}>
        <h4>Invalid property ID</h4>
        <Button onClick={() => navigate('/dashboard/properties')}>
          Go back to properties
        </Button>
      </Card>
    );
  }

  const numericPropertyId = typeof propertyId === 'string' ? parseInt(propertyId, 10) : propertyId;

  return (
    <>
      <AddUnitPageHeader
        property={property}
        propertyId={propertyId}
        loading={isLoading || isFetching}
      />

      <PropertyOverview
        property={property}
        loading={isLoading || isFetching}
        collapsed={getSectionState('overview')}
        onToggleCollapse={() => toggleSection('overview')}
        showMap={true}
      />

      <AddUnitFormSection
        propertyId={propertyId}
        collapsed={getSectionState('addUnit')}
        onToggleCollapse={() => toggleSection('addUnit')}
        onUnitsAdded={handleUnitsAdded}
        onClear={handleFormClear}
      />

      <ListOfUnits
        key={refreshTrigger} // Це примусово перемонтує компонент при зміні
        title="List of units"
        propertyId={propertyId}
        onUnitAction={handleUnitAction}
        onAddUnit={handleAddUnit}
        itemsPerPage={5}
      />

      {/* Модаль для додавання тенанта */}
      {addTenantModal.isOpen && addTenantModal.unitId && (
        <CreateLeaseInviteModal
          isOpen={addTenantModal.isOpen}
          onClose={handleCloseAddTenantModal}
          propertyId={numericPropertyId}
          unitId={addTenantModal.unitId}
          unitNumber={addTenantModal.unitNumber}
          onSuccess={handleTenantAdded}
        />
      )}
    </>
  );
};

export default AddUnitPage;
